import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Globe, ExternalLink, Save, Search, CheckCircle2, Bell, Trash2 } from 'lucide-react';
import { useClients } from '@/hooks/data/useClients';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  useStartExternalSearch,
  useExternalSearchBatch,
  useSaveExternalAsInterest,
  useSavedSearches,
  useCreateSavedSearch,
  useArchiveSavedSearch,
  type SearchCriteria,
  type ExternalSearchResultRow,
  type SavedSearchRow,
} from '@/hooks/data/useExternalSearch';
import { formatDateTime, formatMoney, formatNumber } from '@/lib/formatLocale';

interface RechercheSearch {
  batch?: string;
}

export const Route = createFileRoute('/_app/recherche')({
  // Ausgeblendet seit 2026-09-13. Diese Seite fragte willhaben, ImmoScout24
  // und Immowelt ab, und alle drei untersagen das automatische Auslesen; der
  // Server nimmt keine Suche mehr an (apps/api/src/lib/recherchePortals.ts).
  // Ein altes Lesezeichen oder ein ?batch=-Link aus einer Zoki-Antwort landet
  // beim Radar. Der Code bleibt, damit ein Portal mit Erlaubnis zurückkommt.
  beforeLoad: () => {
    throw redirect({ to: '/radar' });
  },
  validateSearch: (s: Record<string, unknown>): RechercheSearch => {
    if (typeof s.batch === 'string' && s.batch.length > 0) return { batch: s.batch };
    return {};
  },
  component: RecherchePage,
});

const SITES = [
  { key: 'willhaben', label: 'Willhaben.at' },
  { key: 'immoscout24', label: 'ImmobilienScout24.at' },
  { key: 'immowelt', label: 'Immowelt.at' },
] as const;

const BUCKET_ORDER: ExternalSearchResultRow['match_bucket'][] = ['match', 'partial', 'maybe'];

function RecherchePage() {
  const { t } = useTranslation();
  const { data: clients = [] } = useClients({ limit: 2000 });
  const start = useStartExternalSearch();
  const search = Route.useSearch();

  const [batchId, setBatchId] = useState<string | null>(search.batch ?? null);
  const [clientId, setClientId] = useState<string>('');
  const [propertyType, setPropertyType] = useState<'haus' | 'wohnung'>('haus');
  const [district, setDistrict] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sizeMin, setSizeMin] = useState('');
  const [sizeMax, setSizeMax] = useState('');
  const [rooms, setRooms] = useState('');
  const [selectedSites, setSelectedSites] = useState<string[]>([
    'willhaben',
    'immoscout24',
    'immowelt',
  ]);

  const { data: batchData } = useExternalSearchBatch(batchId);
  const save = useSaveExternalAsInterest(batchId);
  const { data: savedData } = useSavedSearches();
  const createSaved = useCreateSavedSearch();
  const archiveSaved = useArchiveSavedSearch();

  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) =>
          ['kupac', 'najmoprimac', 'interessent'].includes(c.client_type),
        )
        .map((c) => ({
          value: c.id,
          label: [c.title, c.first_name, c.last_name].filter(Boolean).join(' '),
          secondary: c.phone,
        })),
    [clients],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSites.length === 0) {
      toast.error(t('recherche.errors.no_sites', 'Mindestens eine Plattform wählen'));
      return;
    }
    const criteria: SearchCriteria = {
      propertyType,
      sites: selectedSites,
      ...(district ? { district: district.trim() } : {}),
      ...(priceMax ? { priceMax: Number(priceMax.replace(/\D/g, '')) } : {}),
      ...(sizeMin ? { sizeMin: Number(sizeMin) } : {}),
      ...(sizeMax ? { sizeMax: Number(sizeMax) } : {}),
      ...(rooms ? { rooms: Number(rooms) } : {}),
    };
    try {
      const { batch_id } = await start.mutateAsync({
        clientId: clientId || undefined,
        criteria,
      });
      setBatchId(batch_id);
      toast.success(t('recherche.started', 'Suche gestartet …'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const resultsByBucket = useMemo(() => {
    const map: Record<string, ExternalSearchResultRow[]> = { match: [], partial: [], maybe: [] };
    for (const r of batchData?.results ?? []) {
      map[r.match_bucket]!.push(r);
    }
    return map;
  }, [batchData?.results]);

  const progressPct =
    batchData?.batch && batchData.batch.progress_total > 0
      ? Math.round((batchData.batch.progress_done / batchData.batch.progress_total) * 100)
      : 0;

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink inline-flex items-center gap-2">
          <Globe className="h-5 w-5 text-ink-3" />
          {t('recherche.title', 'Recherche')}
        </h2>
        <p className="text-sm text-ink-3">
          {t(
            'recherche.subtitle',
            'Findet externe Immobilien-Anzeigen (nur Privatverkäufer) auf Willhaben, ImmoScout24, Immowelt.',
          )}
        </p>
      </header>

      <form className="fm-card p-5 space-y-4" onSubmit={onSubmit}>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>{t('recherche.fields.client', 'Kunde (optional)')}</Label>
            <SearchablePicker
              value={clientId}
              onChange={setClientId}
              options={clientOptions}
              placeholder={t('recherche.fields.client_pick', 'Kunde wählen …')}
            />
          </div>
          <div>
            <Label>{t('recherche.fields.type', 'Typ')} *</Label>
            <Select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value as 'haus' | 'wohnung')}
              required
            >
              <option value="haus">{t('recherche.type.haus', 'Haus')}</option>
              <option value="wohnung">{t('recherche.type.wohnung', 'Wohnung')}</option>
            </Select>
          </div>
          <div>
            <Label>{t('recherche.fields.district', 'PLZ / Bezirk')}</Label>
            <Input
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="z.B. 1130"
            />
          </div>
          <div>
            <Label>{t('recherche.fields.price_max', 'Preis bis (EUR)')}</Label>
            <Input
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              placeholder="800000"
              inputMode="numeric"
            />
          </div>
          <div>
            <Label>{t('recherche.fields.size_min', 'Größe ab (m²)')}</Label>
            <Input
              value={sizeMin}
              onChange={(e) => setSizeMin(e.target.value)}
              placeholder="150"
              inputMode="numeric"
            />
          </div>
          <div>
            <Label>{t('recherche.fields.size_max', 'Größe bis (m²)')}</Label>
            <Input
              value={sizeMax}
              onChange={(e) => setSizeMax(e.target.value)}
              placeholder="200"
              inputMode="numeric"
            />
          </div>
          <div>
            <Label>{t('recherche.fields.rooms', 'Mindest-Zimmer')}</Label>
            <Input
              value={rooms}
              onChange={(e) => setRooms(e.target.value)}
              placeholder="4"
              inputMode="decimal"
            />
          </div>
        </div>

        <div>
          <Label>{t('recherche.fields.sites', 'Plattformen')} *</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {SITES.map((s) => {
              const active = selectedSites.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() =>
                    setSelectedSites((prev) =>
                      prev.includes(s.key) ? prev.filter((x) => x !== s.key) : [...prev, s.key],
                    )
                  }
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    active
                      ? 'border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold)/0.12)] text-[hsl(var(--brand-gold-deep))]'
                      : 'border-line bg-card text-ink-3 hover:text-ink'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (selectedSites.length === 0) {
                toast.error(t('recherche.errors.no_sites', 'Mindestens eine Plattform wählen'));
                return;
              }
              const defaultName =
                (clientId && clientOptions.find((o) => o.value === clientId)?.label) ||
                `${propertyType === 'haus' ? 'Haus' : 'Wohnung'}${district ? ` · ${district}` : ''}${priceMax ? ` · ≤ ${priceMax}` : ''}`;
              const name = window.prompt(
                t('recherche.save_daily.prompt', 'Name für diese tägliche Suche:'),
                defaultName,
              );
              if (!name?.trim()) return;
              const criteria: SearchCriteria = {
                propertyType,
                sites: selectedSites,
                ...(district ? { district: district.trim() } : {}),
                ...(priceMax ? { priceMax: Number(priceMax.replace(/\D/g, '')) } : {}),
                ...(sizeMin ? { sizeMin: Number(sizeMin) } : {}),
                ...(sizeMax ? { sizeMax: Number(sizeMax) } : {}),
                ...(rooms ? { rooms: Number(rooms) } : {}),
              };
              createSaved.mutate(
                { name: name.trim(), clientId: clientId || undefined, criteria },
                {
                  onSuccess: () =>
                    toast.success(
                      t(
                        'recherche.save_daily.created',
                        'Tägliche Suche gespeichert — wird jeden Morgen erneut ausgeführt.',
                      ),
                    ),
                  onError: (e) => toast.error((e as Error).message),
                },
              );
            }}
            disabled={createSaved.isPending}
          >
            <Bell className="me-1.5 h-4 w-4" />
            {t('recherche.save_daily.button', 'Als tägliche Suche speichern')}
          </Button>
          <Button type="submit" disabled={start.isPending}>
            <Search className="me-1.5 h-4 w-4" />
            {start.isPending
              ? t('recherche.starting', 'Wird gestartet …')
              : t('recherche.submit', 'Suche starten')}
          </Button>
        </div>
      </form>

      {savedData && savedData.items.length > 0 && (
        <div className="fm-card p-5 space-y-3">
          <h3 className="font-display text-base font-medium text-ink inline-flex items-center gap-2">
            <Bell className="h-4 w-4 text-[hsl(var(--brand-gold-deep))]" />
            {t('recherche.saved.title', 'Meine täglichen Suchen')}
          </h3>
          <p className="text-xs text-ink-3">
            {t(
              'recherche.saved.hint',
              'Jeden Morgen um 06:00 Uhr Wien erneut ausgeführt. Push-Benachrichtigung bei neuen Privattreffern.',
            )}
          </p>
          <ul className="divide-y divide-line">
            {savedData.items.map((s) => (
              <SavedSearchListItem
                key={s.id}
                row={s}
                onArchive={() =>
                  archiveSaved.mutate(s.id, {
                    onSuccess: () =>
                      toast.success(
                        t('recherche.saved.archived', 'Suche gelöscht'),
                      ),
                  })
                }
              />
            ))}
          </ul>
        </div>
      )}

      {batchData?.batch && (
        <div className="fm-card p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-ink">
              {batchData.batch.status === 'in_progress' || batchData.batch.status === 'queued'
                ? t('recherche.progress.running', 'Suche läuft …')
                : batchData.batch.status === 'done'
                  ? t('recherche.progress.done', 'Suche abgeschlossen')
                  : t('recherche.progress.failed', 'Suche fehlgeschlagen')}
            </p>
            <p className="text-xs text-ink-3 font-mono">
              {batchData.batch.progress_done}/{batchData.batch.progress_total} ·{' '}
              {progressPct}%
            </p>
          </div>
          <div className="h-1.5 w-full rounded-full bg-paper-2 overflow-hidden">
            <div
              className="h-full bg-[hsl(var(--brand-gold))] transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {batchData.batch.error_message && (
            <p className="text-xs text-[hsl(var(--danger))]">
              {batchData.batch.error_message}
            </p>
          )}
        </div>
      )}

      {batchData &&
        BUCKET_ORDER.map((bucket) => {
          const rows = resultsByBucket[bucket] ?? [];
          if (rows.length === 0) return null;
          return (
            <div key={bucket} className="fm-card p-5 space-y-3">
              <h3 className="font-display text-base font-medium text-ink inline-flex items-center gap-2">
                {bucket === 'match' && '🟢'}
                {bucket === 'partial' && '🟡'}
                {bucket === 'maybe' && '🔵'}
                {t(`recherche.bucket.${bucket}`, bucket)} ({rows.length})
              </h3>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {rows.map((r) => (
                  <ResultCard
                    key={r.id}
                    result={r}
                    canSave={!!batchData.batch.client_id}
                    saved={!!r.saved_at}
                    onSave={() => save.mutate(r.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}

      {batchData && batchData.results.length === 0 && batchData.batch.status === 'done' && (
        <div className="fm-card p-8 text-center text-sm text-ink-3">
          {t(
            'recherche.empty',
            'Keine privaten Anzeigen passend zu deinen Kriterien gefunden. Versuche die Filter zu lockern oder andere Plattformen zu wählen.',
          )}
        </div>
      )}
    </section>
  );
}

function SavedSearchListItem({
  row,
  onArchive,
}: {
  row: SavedSearchRow;
  onArchive: () => void;
}) {
  const { t, i18n } = useTranslation();
  let criteria: SearchCriteria | null = null;
  try {
    criteria = JSON.parse(row.criteria_json) as SearchCriteria;
  } catch {
    /* tolerate corrupt rows */
  }
  return (
    <li className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink truncate">{row.name}</p>
        <p className="text-[11px] text-ink-3 truncate">
          {criteria
            ? [
                criteria.propertyType === 'haus' ? 'Haus' : 'Wohnung',
                criteria.district,
                criteria.priceMax ? `≤ ${formatMoney(criteria.priceMax)}` : null,
                criteria.sizeMin ? `≥ ${criteria.sizeMin} m²` : null,
                criteria.rooms ? `${criteria.rooms} Zi` : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : '—'}
        </p>
        <p className="text-[10px] text-ink-3 font-mono">
          {row.last_run_at
            ? t('recherche.saved.last_run', 'Zuletzt: {{date}} · {{count}} Treffer', {
                date: formatDateTime(row.last_run_at, {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                }),
                count: row.last_match_count,
              })
            : t('recherche.saved.never_run', 'Noch nicht ausgeführt')}
        </p>
      </div>
      <button
        type="button"
        onClick={onArchive}
        className="shrink-0 rounded-md border border-line p-1.5 text-ink-3 hover:text-[hsl(var(--danger))] hover:border-[hsl(var(--danger)/0.5)]"
        aria-label={t('recherche.saved.archive', 'Löschen')}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function ResultCard({
  result,
  canSave,
  saved,
  onSave,
}: {
  result: ExternalSearchResultRow;
  canSave: boolean;
  saved: boolean;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  return (
    <article className="rounded-xl border border-line bg-card overflow-hidden">
      {result.image_url ? (
        <img
          src={result.image_url}
          alt={result.title}
          className="h-36 w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="h-36 w-full bg-paper-2" />
      )}
      <div className="p-3 space-y-2">
        <p className="font-medium text-ink line-clamp-2 text-sm">{result.title}</p>
        <div className="flex flex-wrap gap-1.5 text-[11px] text-ink-2">
          {result.price_eur && (
            <span className="font-mono font-semibold">
              € {formatNumber(result.price_eur)}
            </span>
          )}
          {result.size_m2 && <span>· {result.size_m2} m²</span>}
          {result.rooms_count && <span>· {result.rooms_count} Zi</span>}
        </div>
        <div className="flex flex-wrap gap-1.5 text-[11px] text-ink-3">
          {result.postal_code && <span>{result.postal_code}</span>}
          <span className="ms-auto fm-pill fm-pill-neutral">{result.source_site}</span>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <a
            href={result.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-ink-2 hover:text-ink"
          >
            <ExternalLink className="h-3 w-3" />
            {t('recherche.actions.open', 'Original')}
          </a>
          {canSave && !saved && (
            <button
              type="button"
              onClick={onSave}
              className="ms-auto inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--brand-gold-deep))] hover:underline"
            >
              <Save className="h-3 w-3" />
              {t('recherche.actions.save', 'Speichern')}
            </button>
          )}
          {saved && (
            <span className="ms-auto inline-flex items-center gap-1 text-xs text-[hsl(var(--positive))]">
              <CheckCircle2 className="h-3 w-3" />
              {t('recherche.actions.saved', 'Gespeichert')}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
